import type React from 'react';
import { useHistory } from 'react-router-dom';

import { Box, ButtonGroup, Divider, Heading, Icon, Paragraph, TextButton, Upsell } from '@zillow/constellation-10';
import { IconPiggyBankFilled } from '@zillow/constellation-icons';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import { Lease, Property } from '../../models';
import LeaseSummary from '../../components/LeaseSummary';
import ProgressAndSummaryHeader from '../../components/ProgressAndSummaryHeader';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import SummaryTitle from '../../components/LeaseSummary/SummaryTitle';
import { onPrepareForLeaseSigningMobileHandoff } from '../../utils/mobileHandoffFunctions';
import { MOBILE_HANDOFF_TYPES } from '../../constants/stringConstants';
import ManageYourLeaseUpsell, { shouldShowManageLeaseUpsell } from '../../components/Widgets/ManageYourLeaseUpsell';
import { emitSaveForLaterEvent } from '../../../analytics/events';
import { useLeaseSummaryQuery, usePropertyInfoQuery, useWebAnalyticsForListingQuery } from '../../utils/queries';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import { useLeasingUrlParams } from '../../hooks/useLeasingUrlParams';
import { SetUpPaymentsLink } from '../../components/Widgets/SetUpPaymentsLink';

type Props = {
  lease: Lease;
};

// Deprecated once using cosmos
function ListView({ lease }: Props): JSX.Element {
  const history = useHistory();
  const { user } = useRentalManagerUser();
  const { leaseId, propertyId } = useLeasingUrlParams();

  const propertyQuery = usePropertyInfoQuery(propertyId, !!propertyId);

  const property = propertyQuery.data ? new Property(propertyQuery.data) : undefined;

  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;

  const leaseSummaryQuery = useLeaseSummaryQuery(leaseId, LeaseArtifactType.LEASE);

  const summaryActions = leaseSummaryQuery.data?.summaryActions || [];
  const summaryText = leaseSummaryQuery.data?.summaryText || '';

  function onSaveForLaterClick(): void {
    emitSaveForLaterEvent({ user, listing: zrm_listing_info });
    history.push(Lease.getDashboardPath());
    onPrepareForLeaseSigningMobileHandoff(MOBILE_HANDOFF_TYPES.RETURN_TO_DASHBOARD);
  }

  function renderPrimaryActionTop(): React.ReactNode {
    return (
      summaryActions && (
        <LeaseActionButtonGroup
          leaseActions={summaryActions}
          lease={lease}
          property={property}
          data-test="summary-action"
          isTop
        />
      )
    );
  }

  function renderPrimaryActionBottom(): React.ReactNode {
    return (
      summaryActions && (
        <LeaseActionButtonGroup
          leaseActions={summaryActions}
          lease={lease}
          property={property}
          data-test="summary-action"
          isTop={false}
        />
      )
    );
  }

  function renderSaveForLater(): React.ReactNode {
    return <TextButton onClick={onSaveForLaterClick}>Save for later</TextButton>;
  }

  function renderCtaAndSummary(): React.ReactNode {
    const propertyAddress = property?.streetAddress;
    const personFirstName = null;
    const leaseStartDate = lease?.getFriendlyStartDate();
    const hasColandlord = lease?.hasColandlord();

    return (
      <>
        {lease?.isEligibleForLeaseRenewal() && shouldShowManageLeaseUpsell(lease) && (
          <Box css={{ marginBottom: 'tight' }}>
            <Heading
              level={2}
              css={{
                textStyle:
                  'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
                marginBottom: 'tighter',
              }}
            >
              Next step
            </Heading>
            <ManageYourLeaseUpsell lease={lease} />
          </Box>
        )}
        {lease &&
          (lease.status === LeaseStatus.ACTIVE || lease.status === LeaseStatus.SIGNING_COMPLETE) &&
          !lease.isEligibleForLeaseRenewal() && (
            <>
              <Heading
                level={2}
                css={{
                  textStyle:
                    'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
                  marginBottom: 'tighter',
                }}
              >
                Next step
              </Heading>
              <Upsell
                header={<Heading level={3}>Collect move-in costs</Heading>}
                body={
                  <Paragraph>
                    Request payment of your move-in costs (which include the security deposit, prorated rent, and any
                    move-in fees) through Zillow Payments.
                  </Paragraph>
                }
                media={
                  <Icon size="lg" role="img">
                    <IconPiggyBankFilled />
                  </Icon>
                }
                action={<SetUpPaymentsLink lease={lease} property={property} />}
                tone="soft"
                elevated={false}
                closeable={false}
                css={{ marginBottom: 'tight' }}
              />
            </>
          )}
        <SummaryTitle {...{ summaryText, propertyAddress, personFirstName, leaseStartDate, hasColandlord }} />
        <LeaseSummary supportEdit cta={renderPrimaryActionTop()} key="summary" isColandlord={false} />
        <ButtonGroup aria-label="summary actions" css={{ marginTop: 'tight' }}>
          {renderPrimaryActionBottom()}
          {renderSaveForLater()}
        </ButtonGroup>
      </>
    );
  }

  return (
    <>
      <ProgressAndSummaryHeader />
      <Divider css={{ marginTop: 'tight', marginBottom: 'tight' }} />
      <div className="ListView">{renderCtaAndSummary()}</div>
    </>
  );
}

export default ListView;
